import { useState } from "react";
import { Baby, CalendarDays, Heart, HeartHandshake } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { ChoiceCard } from "../ChoiceCard";
import { daysUntil, formatLongDay, isPastDate } from "@/lib/onboarding/defaults";
import { displayNames } from "@/lib/onboarding/buildPreviewEvent";
import type { OnboardingAnswers, OnboardingType } from "@/lib/onboarding/types";

export function StepTitle({ children, id = "onboarding-step-title" }: { children: string; id?: string }) {
  return (
    <h1 id={id} className="mb-6 text-2xl font-semibold leading-tight sm:text-3xl" data-testid="onboarding-step-title">
      {children}
    </h1>
  );
}

/* ------------------------------------------------------------------ W00 */

export function EntryStep() {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 pt-8 text-center sm:pt-16">
      <Heart className="mx-auto h-10 w-10 text-pink-500" aria-hidden="true" />
      <StepTitle>{t("onboarding.entry.title")}</StepTitle>
      <p className="text-muted-foreground">{t("onboarding.entry.subtitle")}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ W01 */

const TYPE_ICONS: Record<OnboardingType, JSX.Element> = {
  wedding: <Heart className="h-7 w-7" />,
  baptism: <Baby className="h-7 w-7" />,
  wedding_baptism: <HeartHandshake className="h-7 w-7" />,
};

export function TypeStep({ value, onSelect }: { value: OnboardingType | null; onSelect: (type: OnboardingType) => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <StepTitle>{t("onboarding.type.title")}</StepTitle>
      <div className="space-y-3" role="radiogroup" aria-labelledby="onboarding-step-title">
        {(["wedding", "baptism", "wedding_baptism"] as OnboardingType[]).map((type) => (
          <ChoiceCard
            key={type}
            selected={value === type}
            onSelect={() => onSelect(type)}
            title={t(`onboarding.type.${type}`)}
            description={t(`onboarding.type.${type}Hint`)}
            icon={TYPE_ICONS[type]}
            testId={`onboarding-type-${type}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ W02 */

interface PeopleStepProps {
  answers: OnboardingAnswers;
  errors: Record<string, boolean>;
  onChange: (changes: Partial<Pick<OnboardingAnswers, "bride" | "groom" | "children" | "parents">>) => void;
}

function NameField({
  id,
  label,
  value,
  invalid,
  onChange,
  autoFocus,
}: {
  id: string;
  label: string;
  value: string;
  invalid?: boolean;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-base">
        {label}
      </Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 text-lg"
        autoComplete="given-name"
        maxLength={60}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${id}-error` : undefined}
        data-testid={id}
        autoFocus={autoFocus}
      />
      {invalid && (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {t("onboarding.people.required")}
        </p>
      )}
    </div>
  );
}

export function PeopleStep({ answers, errors, onChange }: PeopleStepProps) {
  const { t } = useTranslation();
  const [showParents, setShowParents] = useState(Boolean(answers.parents?.mother || answers.parents?.father));
  const hasWedding = answers.type !== "baptism";
  const hasChild = answers.type !== "wedding";
  const children = answers.children.length > 0 ? answers.children : [""];
  const setChild = (index: number, value: string) => {
    const next = [...children];
    next[index] = value;
    onChange({ children: next });
  };
  const couple = displayNames({ ...answers, type: "wedding" });
  const kids = displayNames({ ...answers, type: "baptism" });

  return (
    <div className="space-y-5">
      <StepTitle>{t(`onboarding.people.title.${answers.type}`)}</StepTitle>
      {hasWedding && (
        <>
          <NameField
            id="onboarding-bride"
            label={t("onboarding.people.bride")}
            value={answers.bride ?? ""}
            invalid={errors.bride}
            onChange={(bride) => onChange({ bride })}
            autoFocus
          />
          <NameField
            id="onboarding-groom"
            label={t("onboarding.people.groom")}
            value={answers.groom ?? ""}
            invalid={errors.groom}
            onChange={(groom) => onChange({ groom })}
          />
        </>
      )}
      {hasChild && (
        <>
          <NameField
            id="onboarding-child-0"
            label={t("onboarding.people.child")}
            value={children[0] ?? ""}
            invalid={errors.children}
            onChange={(value) => setChild(0, value)}
            autoFocus={!hasWedding}
          />
          {children.length > 1 ? (
            <div className="space-y-1">
              <NameField
                id="onboarding-child-1"
                label={t("onboarding.people.child2")}
                value={children[1] ?? ""}
                onChange={(value) => setChild(1, value)}
              />
              <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={() => onChange({ children: [children[0]] })}>
                {t("onboarding.people.removeChild")}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-pink-600"
              onClick={() => onChange({ children: [children[0] ?? "", ""] })}
              data-testid="onboarding-add-child"
            >
              {t("onboarding.people.addChild")}
            </Button>
          )}
        </>
      )}
      {answers.type === "baptism" &&
        (showParents ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <NameField
              id="onboarding-mother"
              label={t("onboarding.people.mother")}
              value={answers.parents?.mother ?? ""}
              onChange={(mother) => onChange({ parents: { mother, father: answers.parents?.father ?? null } })}
            />
            <NameField
              id="onboarding-father"
              label={t("onboarding.people.father")}
              value={answers.parents?.father ?? ""}
              onChange={(father) => onChange({ parents: { mother: answers.parents?.mother ?? null, father } })}
            />
          </div>
        ) : (
          <div>
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-pink-600"
              onClick={() => setShowParents(true)}
              data-testid="onboarding-add-parents"
            >
              {t("onboarding.people.addParents")}
            </Button>
          </div>
        ))}

      {(couple || kids) && (
        <div className="rounded-2xl bg-white/70 p-5 text-center shadow-sm dark:bg-white/5" aria-live="polite" data-testid="onboarding-names-preview">
          {hasWedding && couple && <p className="font-serif text-2xl">{couple}</p>}
          {answers.type === "wedding_baptism" && couple && kids && (
            <p className="text-sm italic text-muted-foreground">{t("onboarding.people.togetherWith")}</p>
          )}
          {hasChild && kids && <p className="font-serif text-2xl">{kids}</p>}
        </div>
      )}
      <p className="text-sm text-muted-foreground">{t("onboarding.people.hint")}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ W03 */

interface DateStepProps {
  answers: OnboardingAnswers;
  locale: string;
  invalid: boolean;
  onChange: (date: string | null, tbd: boolean) => void;
}

export function DateStep({ answers, locale, invalid, onChange }: DateStepProps) {
  const { t } = useTranslation();
  const days = daysUntil(answers.date);
  const reward = answers.date ? formatLongDay(answers.date, locale) : "";
  return (
    <div className="space-y-5">
      <StepTitle>{t(`onboarding.date.title.${answers.type}`)}</StepTitle>
      <div className="space-y-1.5">
        <Label htmlFor="onboarding-date" className="text-base">
          {t("onboarding.date.label")}
        </Label>
        <Input
          id="onboarding-date"
          type="date"
          value={answers.date ?? ""}
          onChange={(e) => onChange(e.target.value || null, false)}
          className="h-12 text-lg"
          aria-invalid={invalid || undefined}
          data-testid="onboarding-date"
          autoFocus
        />
        {invalid && <p className="text-sm text-destructive">{t("onboarding.date.invalid")}</p>}
      </div>

      {answers.date && reward && (
        <div className="flex items-start gap-3 rounded-2xl bg-white/70 p-4 shadow-sm dark:bg-white/5" aria-live="polite" data-testid="onboarding-date-reward">
          <CalendarDays className="mt-0.5 h-5 w-5 text-pink-500" aria-hidden="true" />
          <div>
            <p className="font-semibold">{reward}</p>
            {days !== null && days > 1 && <p className="text-sm text-muted-foreground">{t("onboarding.date.daysLeft", { days })}</p>}
            {days === 1 && <p className="text-sm text-muted-foreground">{t("onboarding.date.oneDayLeft")}</p>}
            {days === 0 && <p className="text-sm text-muted-foreground">{t("onboarding.date.today")}</p>}
            {isPastDate(answers.date) && (
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400" data-testid="onboarding-date-past">
                {t("onboarding.date.past")}
              </p>
            )}
          </div>
        </div>
      )}

      <ChoiceCard
        role="checkbox"
        selected={answers.dateTbd && !answers.date}
        onSelect={() => onChange(null, !(answers.dateTbd && !answers.date))}
        title={t("onboarding.date.tbd")}
        testId="onboarding-date-tbd"
      />
      {answers.dateTbd && !answers.date && <p className="text-sm text-muted-foreground">{t("onboarding.date.tbdNote")}</p>}
    </div>
  );
}
